(() => {
  if (window.DeebAccessoryQuantitySafety) return;

  const ROOT_SELECTOR = 'product-info[data-product-family="accessory"]';
  const GUARD_SELECTOR = '[data-deeb-accessory-quantity-guard]';
  const INPUT_SELECTOR = '.quantity__input';

  class AccessoryQuantityGuard {
    constructor(root) {
      this.root = root;
      this.guard = root.querySelector(GUARD_SELECTOR);
      this.input = this.guard?.querySelector(INPUT_SELECTOR);
      this.form = this.input?.form || null;
      this.productForm = this.form?.closest('product-form') || null;
      this.submitButton = this.productForm?.querySelector('[type="submit"][name="add"]') || null;
      this.invalidQuantityMessage = String(
        this.guard?.dataset.invalidQuantityMessage || ''
      ).trim();
      this.guardOwnsSubmitState = false;
      this.previousSubmitState = null;
      this.destroyed = false;

      if (!this.ready) return;

      this.input.required = true;
      this.root.addEventListener('input', this.handleQuantityEvent, true);
      this.root.addEventListener('change', this.handleQuantityEvent, true);
      this.root.addEventListener('focusout', this.handleQuantityBlur, true);
      this.root.addEventListener('submit', this.handleSubmit, true);

      this.validate({ showMessage: false });
    }

    get ready() {
      return Boolean(
        !this.destroyed &&
        this.root?.isConnected &&
        this.input &&
        this.form &&
        this.productForm &&
        this.submitButton &&
        this.invalidQuantityMessage
      );
    }

    matchesCurrentControls() {
      if (this.destroyed || !this.root?.isConnected) return false;

      const guard = this.root.querySelector(GUARD_SELECTOR);
      const input = guard?.querySelector(INPUT_SELECTOR);
      const form = input?.form || null;
      const productForm = form?.closest('product-form') || null;
      const submitButton =
        productForm?.querySelector('[type="submit"][name="add"]') || null;

      return (
        guard === this.guard &&
        input === this.input &&
        form === this.form &&
        productForm === this.productForm &&
        submitButton === this.submitButton
      );
    }

    handleQuantityEvent = (event) => {
      if (event.target !== this.input) return;
      this.validate({ showMessage: event.type === 'change' });
    };

    handleQuantityBlur = (event) => {
      if (event.target !== this.input) return;
      this.validate({ showMessage: true });
    };

    handleSubmit = (event) => {
      if (event.target !== this.form) return;
      if (this.validate({ showMessage: true })) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.input.focus();
    };

    validate({ showMessage = false } = {}) {
      if (!this.ready) return true;

      const quantity = this.input.valueAsNumber;
      const valid = Number.isFinite(quantity) && this.input.checkValidity();

      if (valid) {
        this.input.removeAttribute('aria-invalid');
        this.guard.removeAttribute('data-quantity-invalid');
        this.releaseSubmitState();
        this.productForm.handleErrorMessage?.();
        return true;
      }

      this.input.setAttribute('aria-invalid', 'true');
      this.guard.setAttribute('data-quantity-invalid', 'true');
      this.takeSubmitState();

      if (showMessage) {
        const message = this.input.validationMessage || this.invalidQuantityMessage;
        this.productForm.handleErrorMessage?.(message);
      }

      return false;
    }

    takeSubmitState() {
      if (!this.ready) return;

      if (!this.guardOwnsSubmitState) {
        this.previousSubmitState = {
          disabled: this.submitButton.disabled,
          ariaDisabled: this.submitButton.getAttribute('aria-disabled'),
        };
        this.guardOwnsSubmitState = true;
      }

      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-disabled', 'true');
    }

    releaseSubmitState() {
      if (this.destroyed || !this.submitButton || !this.guardOwnsSubmitState) return;

      if (this.previousSubmitState?.disabled) {
        this.submitButton.disabled = true;
      } else {
        this.submitButton.removeAttribute('disabled');
      }

      if (this.previousSubmitState?.ariaDisabled === null) {
        this.submitButton.removeAttribute('aria-disabled');
      } else if (this.previousSubmitState) {
        this.submitButton.setAttribute('aria-disabled', this.previousSubmitState.ariaDisabled);
      }

      this.guardOwnsSubmitState = false;
      this.previousSubmitState = null;
    }

    resetSubmitOwnership() {
      this.guardOwnsSubmitState = false;
      this.previousSubmitState = null;
    }

    destroy() {
      if (this.destroyed) return;

      this.releaseSubmitState();
      this.root?.removeEventListener('input', this.handleQuantityEvent, true);
      this.root?.removeEventListener('change', this.handleQuantityEvent, true);
      this.root?.removeEventListener('focusout', this.handleQuantityBlur, true);
      this.root?.removeEventListener('submit', this.handleSubmit, true);

      this.destroyed = true;
      this.root = null;
      this.guard = null;
      this.input = null;
      this.form = null;
      this.productForm = null;
      this.submitButton = null;
      this.previousSubmitState = null;
      this.guardOwnsSubmitState = false;
    }
  }

  const instances = new WeakMap();
  const activeInstances = new Set();
  const unsubscribers = [];

  const destroyInstance = (root) => {
    const instance = instances.get(root);
    if (!instance) return;

    instance.destroy();
    activeInstances.delete(instance);
    instances.delete(root);
  };

  const initializeRoot = (root) => {
    if (!(root instanceof HTMLElement) || !root.matches(ROOT_SELECTOR)) return null;

    const existing = instances.get(root);
    if (existing?.matchesCurrentControls()) return existing;
    if (existing) destroyInstance(root);

    const instance = new AccessoryQuantityGuard(root);
    if (!instance.ready) {
      instance.destroy();
      return null;
    }

    instances.set(root, instance);
    activeInstances.add(instance);
    return instance;
  };

  const eachInstance = (callback) => {
    activeInstances.forEach((instance) => {
      if (!instance.matchesCurrentControls()) {
        const root = instance.root;
        if (root) destroyInstance(root);
        else activeInstances.delete(instance);
        return;
      }

      callback(instance);
    });
  };

  const validateAll = () => {
    queueMicrotask(() => eachInstance((instance) => instance.validate({ showMessage: false })));
  };

  const resetAndValidateAll = () => {
    queueMicrotask(() =>
      eachInstance((instance) => {
        instance.resetSubmitOwnership();
        instance.validate({ showMessage: false });
      })
    );
  };

  const initializeAll = (scope = document) => {
    if (scope instanceof HTMLElement && scope.matches(ROOT_SELECTOR)) initializeRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initializeRoot);
  };

  const destroyAll = (scope = document) => {
    const roots = [];

    if (scope instanceof HTMLElement && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));

    roots.forEach(destroyInstance);
  };

  const handleProductInfoLoaded = (event) => initializeRoot(event.target);
  const handleSectionLoad = (event) => initializeAll(event.target);
  const handleSectionUnload = (event) => destroyAll(event.target);

  document.addEventListener('product-info:loaded', handleProductInfoLoaded);
  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener('shopify:section:unload', handleSectionUnload);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    const quantityUnsubscribe = subscribe(PUB_SUB_EVENTS.quantityUpdate, validateAll);
    const variantUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, resetAndValidateAll);

    if (typeof quantityUnsubscribe === 'function') unsubscribers.push(quantityUnsubscribe);
    if (typeof variantUnsubscribe === 'function') unsubscribers.push(variantUnsubscribe);
  }

  const destroy = () => {
    activeInstances.forEach((instance) => instance.destroy());
    activeInstances.clear();

    unsubscribers.splice(0).forEach((unsubscribe) => unsubscribe());

    document.removeEventListener('product-info:loaded', handleProductInfoLoaded);
    document.removeEventListener('shopify:section:load', handleSectionLoad);
    document.removeEventListener('shopify:section:unload', handleSectionUnload);

    delete window.DeebAccessoryQuantitySafety;
  };

  initializeAll();

  window.DeebAccessoryQuantitySafety = Object.freeze({
    validate: validateAll,
    destroy,
    get activeInstances() {
      return activeInstances.size;
    },
  });
})();
